import 'prebid.js/modules/genericAnalyticsAdapter.js';

import type {GlobalConfig} from "header-bidding-client-base/modules/config/domain/globalConfig";
import type {ILogger} from "header-bidding-client-base/modules/logger/domain/iLogger";

import type {AdUnits} from "../../../header-bidding/domain/headerBiddingService";
import {BidStatus, IBidEventDTO} from "services-comun-meteored/modules/services/header-bidding/interface";

export class PrebidJSAnalyticsModule {

    public constructor(private pbjs: any, private logger: ILogger) {
    }

    public init(config: GlobalConfig, adUnitMaps: AdUnits){
        this.pbjs.enableAnalytics({
            provider: 'generic',
            options: {
                sampling: config.analytics!.sampling,
                events: {
                    auctionEnd: (request: any) => {
                        return this.getAuctionStats(request, config, adUnitMaps);
                    }
                },
                handler(data: any) {
                    fetch(`${config.domain}/web/events`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(data[0])
                    }).catch((error) => {
                        console.error('Error sending event', error);
                    });
                }
            }
        });

        this.pbjs.onEvent('bidWon', (data: any) => {
            const event: IBidEventDTO = {
                status: BidStatus.WIN,
                id_auction: data.auctionId,
                id: data.requestId,
                id_platform: 1,
                project_hash: config.project.hash,
                config_hash: config.config.hash,
                timestamp: data.responseTimestamp,
                adunit_path: adUnitMaps[data.adUnitCode].adUnitPath,
                adunit_type: adUnitMaps[data.adUnitCode].adUnitType,
                bidder: data.bidder,
                adunit_div_id: data.adUnitCode,
                data: {
                    ad_id: data.adId,
                    cpm: data.cpm,
                    original_cpm: data.originalCpm,
                    time_to_respond: data.timeToRespond,
                },
            }

            fetch(`${config.domain}/web/events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify([event])
            }).catch((error) => {
                console.error('Error sending event', error);
            });
        });
    }

    private getAuctionStats(request: any, config: GlobalConfig, adUnitMaps: AdUnits) {
        const bidTimeouts: NodeJS.Dict<number> = {};
        const bidRequests: any[] = request.bidderRequests.flatMap((bidderRequest: any) => {
            return bidderRequest.bids.map((bid: any) => {
                const id = bid.bidId;
                bidTimeouts[id] = bid.timeout;
                return {
                    id: id,
                    status: BidStatus.REQUESTED,
                    id_auction: request.auctionId,
                    id_platform: 1,
                    project_hash: config.project.hash,
                    config_hash: config.config.hash,
                    timestamp: request.timestamp,
                    adunit_path: adUnitMaps[bid.adUnitCode].adUnitPath,
                    adunit_type: adUnitMaps[bid.adUnitCode].adUnitType,
                    bidder: bid.bidder,
                    adunit_div_id: bid.adUnitCode,
                }
            })
        });

        const events: IBidEventDTO[] = [];

        // No bids
        request.noBids.forEach((noBid: any) => {
            const bid = bidRequests.find((bid) => bid.id === noBid.bidId);
            if (bid) {
                bid.status = BidStatus.NOBID;
                events.push({
                    ...bid,
                    status: BidStatus.NOBID,
                })
            }
        });

        // Bids Received
        request.bidsReceived.forEach((bidReceived: any) => {
            const bid = bidRequests.find((bid) => bid.id === bidReceived.requestId);
            if (bid) {
                bid.status = BidStatus.RECEIVED;
                events.push({
                    ...bid,
                    status: BidStatus.RECEIVED,
                    data: {
                        ad_id: bidReceived.adId,
                        cpm: bidReceived.cpm,
                        original_cpm: bidReceived.originalCpm,
                        time_to_respond: bidReceived.timeToRespond,
                        timeout: bidTimeouts[bidReceived.requestId]
                    }
                })
            }
        });

        // Bids Rejected
        request.bidsRejected.forEach((bidRejected: any) => {
            const bid = bidRequests.find((bid) => bid.id === bidRejected.requestId);
            if (bid) {
                bid.status = BidStatus.REJECTED;
                events.push({
                    ...bid,
                    status: BidStatus.REJECTED,
                    data: {
                        reason: bidRejected.reason
                    }
                });
            }
        });

        // Timedout bids
        bidRequests.forEach((bid) => {
            if (bid.status === BidStatus.REQUESTED) {
                events.push({
                    ...bid,
                    status: BidStatus.TIMEDOUT,
                    data: {
                        timeout: bidTimeouts[bid.id]
                    }
                })
            }
            return bid;
        });

        // this.logger.log('[Analytics]', 'Auction stats', events);

        return events;

    }
}
